import { Component } from '@theme/component';
import { fetchConfig, debounce, onAnimationEnd, prefersReducedMotion, resetShimmer } from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @type {string} */
const CART_NOTE_IDLE_EVENT = 'cart-note:idle';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
function qtyCartDebugEnabled() {
  if (typeof window === 'undefined') return false;
  if (/** @type {any} */ (window).__qtyDropdownDebug__) return true;
  try {
    if (window.localStorage?.getItem('qty-dropdown-debug') === '1') return true;
  } catch (_) {
    /* noop */
  }
  try {
    if (window.location?.search?.includes('qty_debug=1')) return true;
  } catch (_) {
    /* noop */
  }
  return false;
}

/** @param {...any} args */
function qtyCartLog(...args) {
  if (!qtyCartDebugEnabled()) return;
  console.log('[cart-items]', ...args);
}

class CartItemsComponent extends Component {
  /** @type {Map<number, number>} */
  #pendingLineQuantities = new Map();

  /** @type {string | null} */
  #pendingCartMorphHtml = null;

  /** @type {boolean} */
  #pendingCartMorphFullRender = false;

  #debouncedOnChange = debounce(this.#onQuantityChange, 200).bind(this);

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, true);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    document.addEventListener(CART_NOTE_IDLE_EVENT, this.#handleCartNoteIdle);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, true);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    document.removeEventListener(CART_NOTE_IDLE_EVENT, this.#handleCartNoteIdle);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    const { quantity, cartLine: line } = event.detail;

    const fromThisComponent =
      event.target instanceof Node && this.contains(/** @type {Node} */ (event.target));

    qtyCartLog('quantity-selector:update received', {
      sectionId: this.sectionId,
      line,
      quantity,
      target: event.target,
      fromThisComponent,
    });

    if (!fromThisComponent) {
      return;
    }

    if (!line) {
      qtyCartLog('ignored: no cartLine in event.detail');
      return;
    }

    qtyCartLog('quantity update', { sectionId: this.sectionId, line, quantity });

    this.#pendingLineQuantities.set(line, quantity);
    this.#markLinePendingInDom(line, quantity);

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];

    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    qtyCartLog('updateQuantity start', {
      sectionId: this.sectionId,
      line: config.line,
      quantity: config.quantity,
      action: config.action,
    });

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          qtyCartLog('updateQuantity: cart API error', {
            sectionId: this.sectionId,
            line,
            quantity,
            errors: parsedResponseText.errors,
          });
          this.#handleCartError(line, parsedResponseText);
          return;
        }

        qtyCartLog('updateQuantity: success', {
          sectionId: this.sectionId,
          line,
          quantity,
        });

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        morphSection(this.sectionId, parsedResponseText.sections[this.sectionId]);
        this.#clearLinePending(line, quantity);

        this.dispatchEvent(
          new CartUpdateEvent({}, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );
      })
      .catch((error) => {
        this.#pendingLineQuantities.delete(line);
        const container = this.querySelector(`[data-quantity-dropdown][data-cart-line="${line}"]`);
        if (container instanceof HTMLElement) {
          container.removeAttribute('data-pending-quantity');
        }
        qtyCartLog('updateQuantity: fetch failed', { sectionId: this.sectionId, line, quantity, error });
        console.error(error);
      })
      .finally(() => {
        qtyCartLog('updateQuantity: finished (re-enabled cart items)', { sectionId: this.sectionId });
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * @param {number} line
   * @param {number} quantity
   */
  #markLinePendingInDom(line, quantity) {
    const container = this.querySelector(`[data-quantity-dropdown][data-cart-line="${line}"]`);
    if (container instanceof HTMLElement) {
      container.setAttribute('data-pending-quantity', String(quantity));
    }
  }

  /**
   * @param {number} line
   * @param {number} quantity
   */
  #clearLinePending(line, quantity) {
    this.#pendingLineQuantities.delete(line);
    queueMicrotask(() => {
      if (typeof window.__qtyDropdownRestorePending === 'function') {
        window.__qtyDropdownRestorePending();
      }
      const container = this.querySelector(`[data-quantity-dropdown][data-cart-line="${line}"]`);
      if (container instanceof HTMLElement) {
        const select = container.querySelector('[data-quantity-select]');
        const serverQty = select ? parseInt(select.value, 10) : NaN;
        if (!isNaN(serverQty) && serverQty === quantity) {
          container.removeAttribute('data-pending-quantity');
        }
      }
    });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    this.#pendingLineQuantities.delete(line);
    const container = this.querySelector(`[data-quantity-dropdown][data-cart-line="${line}"]`);
    if (container instanceof HTMLElement) {
      container.removeAttribute('data-pending-quantity');
    }

    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput =
      quantitySelector?.querySelector('input') || quantitySelector?.querySelector('select');

    if (!quantityInput) throw new Error('Quantity input not found');

    if (quantityInput instanceof HTMLSelectElement) {
      const defaultOption = Array.from(quantityInput.options).find((opt) => opt.defaultSelected);
      quantityInput.value = defaultOption ? defaultOption.value : quantityInput.options[0]?.value || '1';
    } else {
      quantityInput.value = quantityInput.defaultValue;
    }

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Whether the cart note is being edited or saved — defer section morphs so
   * concurrent cart updates do not wipe in-flight input or collapse the accordion.
   *
   * @returns {boolean}
   */
  #isCartNoteInteractionActive() {
    const textarea = document.getElementById('cart-note');
    if (!(textarea instanceof HTMLTextAreaElement)) return false;

    if (document.activeElement === textarea) return true;
    if (textarea.closest('cart-note')?.hasAttribute('data-note-saving')) return true;
    if (textarea.value.trim() !== textarea.defaultValue.trim()) return true;

    return false;
  }

  #handleCartNoteIdle = () => {
    this.#flushPendingCartMorph();
  };

  #flushPendingCartMorph() {
    if (this.#isCartNoteInteractionActive()) return;

    if (this.#pendingCartMorphHtml) {
      const html = this.#pendingCartMorphHtml;
      this.#pendingCartMorphHtml = null;
      this.#pendingCartMorphFullRender = false;
      morphSection(this.sectionId, html);
      return;
    }

    if (this.#pendingCartMorphFullRender) {
      this.#pendingCartMorphFullRender = false;
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  }

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    // Ignore stale section HTML from other flows (e.g. add-to-cart) while a
    // quantity change is in flight — prevents a brief flash back to the old qty.
    if (this.#pendingLineQuantities.size > 0 && event.detail?.data?.source !== 'cart-items-component') {
      qtyCartLog('handleCartUpdate skipped (pending line qty update)', {
        sectionId: this.sectionId,
        source: event.detail?.data?.source,
      });
      return;
    }

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];

    if (this.#isCartNoteInteractionActive()) {
      if (cartItemsHtml) {
        this.#pendingCartMorphHtml = cartItemsHtml;
      } else {
        this.#pendingCartMorphFullRender = true;
      }
      return;
    }

    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
